import { useTranslations } from 'use-intl';
import type { Provenance } from '@/app/queries';
import { Chip } from '@/ui/Chip';

/**
 * Where a number came from, said beside the number.
 *
 * `CLAUDE.md` has required this since the first commit — "`provenance` travels
 * with every stored number and is shown wherever it is", and "estimated series
 * render visually distinct from measured ones". The column was always written
 * and the words were always translated; both read paths dropped it in the
 * SELECT, so nothing downstream could tell a coach whether a readiness figure
 * rested on a match somebody measured or on an estimate the engine made.
 *
 * **A word, not a colour.** The design system reserves semantic colour for data
 * — the load ramp means load and nothing else — so a provenance rendered in
 * colour would compete with the readiness scale for the same glance. It is also
 * not a texture: planned-versus-actual already owns dashed-versus-solid, and a
 * second texture on the same chart would read as a second kind of intention.
 *
 * The short word is what fits a list row; the full phrase — "Derived from the
 * session rating" — is the title and what a screen reader reads, because the
 * phrase is the part that actually explains anything.
 */
export function ProvenanceChip({ value }: { value: Provenance }) {
  const t = useTranslations();
  const full = t(`load.provenance.${value}`);

  return (
    <Chip muted>
      <span title={full} aria-hidden="true">
        {t(`load.provenanceShort.${value}`)}
      </span>
      <span className="sr-only">{full}</span>
    </Chip>
  );
}

/**
 * One line naming what a panel's figures rest on.
 *
 * Twenty-four rows each repeating "Derived from the session rating" is noise;
 * the set, said once, is the thing a coach reads. Ordered weakest first,
 * because the weakest is what the figures are actually worth.
 */
export function ProvenanceNote({
  values,
  className = '',
}: {
  values: readonly Provenance[];
  className?: string;
}) {
  const t = useTranslations();
  const ORDER: Provenance[] = [
    'estimated_from_plan',
    'derived',
    'player_reported',
    'coach_observed',
    'measured',
  ];
  const present = ORDER.filter((p) => values.includes(p));
  if (present.length === 0) return null;

  return (
    <span className={className}>
      {t('load.restsOn', { kinds: present.map((p) => t(`load.provenance.${p}`)).join(' · ') })}
    </span>
  );
}
